import { AlertTriangle, RefreshCw, Home } from 'lucide-react';
import { OrbitIcon } from "@/components/icons/OrbitIcon";
import { Button } from '@/components/ui/button';

interface ErrorFallbackProps {
  error: Error | null;
  /** React's component stack for the throw, when the boundary caught one. */
  componentStack?: string | null;
  onReset?: () => void;
}

/**
 * MKTG-3 — describe an error that has no `message`.
 *
 * This card used to render `error.message` raw. That works right up
 * until something throws an Error with an EMPTY message, and then the
 * red bar renders blank and the crash is invisible — which is exactly
 * what happened to /orbit: react-router's production bundle compiles
 * its nested-`<Router>` invariant down to `invariant(false)` with the
 * message stripped, so the app died behind a blank box.
 *
 * Minified React errors, `throw "a string"`, rejected non-Error values
 * and stripped invariants all land here too. So: fall back through
 * message → name + first stack frame → stringified value, and always
 * say SOMETHING.
 */
function describeError(error: unknown): string {
  if (error == null) return 'No error detail was captured.';

  if (error instanceof Error) {
    const message = error.message?.trim();
    if (message) return message;

    // Messageless Error — the stripped-invariant case. The first
    // meaningful line of the stack is the only identifying detail
    // there is, so surface it rather than an empty box.
    const firstStackLine = error.stack
      ?.split('\n')
      .map((line) => line.trim())
      .find(
        (line) =>
          line && line !== error.name && line !== `${error.name}:`,
      );

    return firstStackLine
      ? `${error.name} (no message) — ${firstStackLine}`
      : `${error.name} was thrown with no message. Check the browser console for the full error.`;
  }

  if (typeof error === 'string') return error;

  try {
    return JSON.stringify(error);
  } catch {
    return String(error);
  }
}

export function ErrorFallback({
  error,
  componentStack,
  onReset,
}: ErrorFallbackProps) {
  const handleGoHome = () => {
    // Use window.location for navigation since we might be outside Router context
    window.location.href = '/';
  };

  const handleRefresh = () => {
    if (onReset) {
      onReset();
    } else {
      window.location.reload();
    }
  };

  const detail = describeError(error);
  // Stacks are noise for a studio mid-shoot but the whole story for
  // whoever's debugging, so they're dev-only.
  const isDev = import.meta.env.DEV;
  const stack = error instanceof Error ? error.stack : null;

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-6">
      <div className="card-glass p-8 max-w-md text-center animate-fade-in">
        <div className="relative mb-6 inline-block">
          <div className="absolute inset-0 bg-destructive/20 rounded-full blur-xl" />
          <div className="relative card-glass p-5 rounded-full">
            <OrbitIcon name="exclamationmark.triangle.fill" fallback={AlertTriangle} className="w-10 h-10 text-destructive" />
          </div>
        </div>

        <h2 className="font-editorial text-2xl md:text-3xl text-foreground leading-none mb-3">
          That didn&apos;t land.
        </h2>

        <p className="text-muted-foreground mb-4">
          Something tripped on the way here. Give it another try — Nova&apos;s on it if this keeps happening.
        </p>

        {/* Always rendered — an empty bar is worse than an ugly one. */}
        <div className="bg-destructive/10 border border-destructive/20 rounded-lg p-3 mb-6 text-left">
          <code className="text-xs text-destructive/80 break-all">{detail}</code>
        </div>

        {isDev && (stack || componentStack) && (
          <details className="mb-6 text-left">
            <summary className="cursor-pointer text-xs text-muted-foreground hover:text-foreground">
              Stack (dev only)
            </summary>
            {stack && (
              <pre className="mt-2 max-h-48 overflow-auto rounded-lg bg-muted/40 p-3 text-[10px] leading-relaxed text-muted-foreground whitespace-pre-wrap break-all">
                {stack}
              </pre>
            )}
            {componentStack && (
              <pre className="mt-2 max-h-48 overflow-auto rounded-lg bg-muted/40 p-3 text-[10px] leading-relaxed text-muted-foreground whitespace-pre-wrap break-all">
                {componentStack}
              </pre>
            )}
          </details>
        )}

        <div className="flex gap-3 justify-center">
          <Button variant="outline" onClick={handleGoHome}>
            <OrbitIcon name="house.fill" fallback={Home} className="w-4 h-4 mr-2" />
            Go Home
          </Button>
          <Button onClick={handleRefresh}>
            <OrbitIcon name="arrow.clockwise" fallback={RefreshCw} className="w-4 h-4 mr-2" />
            Try Again
          </Button>
        </div>
      </div>
    </div>
  );
}
